"use client";

import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useState } from "react";

const Plot = dynamic(() => import("react-plotly.js"), { ssr: false });

type Resp = {
  statistics: Array<{
    data: any[];
    layout: any;
  }>;
};

type Props = {
  /** POST target */
  endpoint?: string;
  /** POST body */
  payload?: unknown;

  /** If you already have the response, pass it to skip fetching */
  initial?: Resp;

  /** Styling */
  className?: string;
  height?: number;

  /** Optional image if the chart fails */
  fallbackImage?: string;

  /** Called on successful fetch */
  onLoaded?: (resp: Resp) => void;
};

export default function SlopeCharts({
  endpoint,
  payload,
  initial,
  className,
  height = 360,
  fallbackImage,
  onLoaded,
}: Props) {
  const [resp, setResp] = useState<Resp | null>(initial ?? null);
  const [loading, setLoading] = useState<boolean>(!initial && !!endpoint);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let abort = false;

    async function run() {
      if (initial) {
        setResp(initial);
        setLoading(false);
        setError(null);
        return;
      }
      if (!endpoint || !payload) {
        setLoading(false);
        setError("Missing endpoint/payload");
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${res.statusText} ${t}`.trim());
        }

        const json = (await res.json()) as Resp;
        if (abort) return;

        setResp(json);
        setLoading(false);
        onLoaded?.(json);
      } catch (e: any) {
        if (abort) return;
        console.error("SlopeCharts fetch failed:", e);
        setLoading(false);
        setError(e?.message || "Failed to load chart.");
      }
    }

    run();
    return () => {
      abort = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(payload), initial]);

  const plot = useMemo(() => {
    if (!resp?.statistics?.length) return null;
    return resp.statistics[0] ?? null; // slope response has one plot in [0]
  }, [resp]);

  // Skeleton
  if (loading) {
    return (
      <div
        className={className}
        style={{
          height,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          background: "#fff",
          color: "#6b7280",
        }}
      >
        Loading chart…
      </div>
    );
  }

  // Error -> optional image fallback
  if (error || !plot) {
    if (fallbackImage) {
      return (
        <div
          className={className}
          style={{
            height,
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          {/* plain <img> to avoid Next/Image layout constraint inside dynamic area */}
          <img
            src={fallbackImage}
            alt="Slope chart fallback"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
      );
    }
    return (
      <div
        className={className}
        style={{
          height,
          border: "1px solid #fecaca",
          background: "#fff1f2",
          color: "#991b1b",
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          padding: 16,
          textAlign: "center",
        }}
      >
        Couldn’t load chart{error ? `: ${error}` : ""}.
      </div>
    );
  }

  const plotKey = `slope-${(plot.data?.length ?? 0)}-${plot.layout?.title ?? ""}`;

  return (
    <div className={className} style={{ background: "#fff", borderRadius: 12 }}>
      <Plot
        key={plotKey}
        data={plot.data}
        layout={{
          ...plot.layout,
          autosize: true,
          height,
          margin: { l: 60, r: 30, t: 50, b: 60 },
          paper_bgcolor: "#ffffff",
          plot_bgcolor: "#ffffff",
          
        }}
        useResizeHandler
        style={{ width: "100%", height }}

        config={{
        responsive: true,
        displayModeBar: true,   // show toolbar
        displaylogo: false,     // hide Plotly logo
        // keep only: toImage (camera) + resetScale2d (home)
        modeBarButtonsToRemove: [
            "zoom2d", "pan2d", "select2d", "lasso2d",
            "zoomIn2d", "zoomOut2d",
            "autoScale2d",                    // keep resetScale2d (home) by NOT listing it
            "hoverClosestCartesian", "hoverCompareCartesian",
            "toggleSpikelines", "toggleHover",
            // you don't need to list any 3D/geo buttons for a 2D chart
            "sendDataToCloud"
        ],
        }}
      />
    </div>
  );
}